import { DocumentText, Notepad2, Trash, ExportCurve } from '@/components/icons';
import { EmptyState } from '@/components/system';
import { Button } from '@/components/ui/button';
import Loading from '@/components/ui/Loading';
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button';
import { formatRelativeToNow } from '@/lib/date';
import { getAnamnesisWordCount } from '@/pages/patients/anamnesis/utils';
import { formatDateTime } from '@/pages/patients/components/formatters';
import type { PatientAnamnesis } from '@/types/patient-anamnesis';

type Props = {
    anamnesis?: PatientAnamnesis | null;
    isLoading: boolean;
    onCreate: () => void;
    onOpen: () => void;
    onDelete: () => void;
};

const formatRelative = (value?: string) => formatRelativeToNow(value, '');

export default function PatientAnamnesisTab({
    anamnesis,
    isLoading,
    onCreate,
    onOpen,
    onDelete,
}: Props) {
    if (isLoading) {
        return (
            <div className="p-6">
                <Loading />
            </div>
        );
    }

    if (!anamnesis) {
        return (
            <EmptyState
                icon={DocumentText}
                title="Nenhuma anamnese criada"
                action={
                    <Button onClick={onCreate}>
                        <Notepad2 className="size-4" />
                        Criar anamnese
                    </Button>
                }
            />
        );
    }

    if (anamnesis.source === 'uploaded') {
        const uploadedWordCount =
            anamnesis.word_count ?? getAnamnesisWordCount(anamnesis.content_html);
        return (
            <div className="p-6">
                <div className="flex flex-col gap-4 rounded-lg border border-border bg-card p-5 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-start gap-3">
                        <span className="mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                            <ExportCurve className="size-5" />
                        </span>
                        <div>
                            <p className="text-sm font-medium text-foreground">
                                {anamnesis.file_name ?? 'Anamnese enviada (arquivo)'}
                            </p>
                            <p className="mt-1 text-xs text-muted-foreground">
                                {uploadedWordCount}{' '}
                                {uploadedWordCount === 1 ? 'palavra' : 'palavras'}
                                {anamnesis.updated_at
                                    ? ` · enviado ${formatRelative(anamnesis.updated_at)}`
                                    : ''}
                            </p>
                        </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                        <Button onClick={onOpen}>Abrir anamnese</Button>
                        <TooltipIconButton
                            label="Excluir anamnese"
                            variant="ghostDestructive"
                            size="icon"
                            onClick={onDelete}
                        >
                            <Trash className="size-4" />
                        </TooltipIconButton>
                    </div>
                </div>
            </div>
        );
    }

    const wordCount = anamnesis.word_count ?? getAnamnesisWordCount(anamnesis.content_html);
    const relative = formatRelative(anamnesis.updated_at);

    return (
        <div className="p-6">
            <div className="flex flex-col gap-4 rounded-lg border border-border bg-card p-5 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-3">
                    <span className="mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-md bg-brand-subtle text-brand">
                        <Notepad2 className="size-5" />
                    </span>
                    <div>
                        <p className="text-sm font-medium text-foreground">Anamnese</p>
                        <p className="mt-1 text-xs text-muted-foreground">
                            {wordCount} {wordCount === 1 ? 'palavra' : 'palavras'}
                            {relative ? ` · editada ${relative}` : ''}
                            {anamnesis.updated_at
                                ? ` (${formatDateTime(anamnesis.updated_at)})`
                                : ''}
                        </p>
                    </div>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                    <Button onClick={onOpen}>Abrir anamnese</Button>
                    <TooltipIconButton
                        label="Excluir anamnese"
                        variant="ghostDestructive"
                        size="icon"
                        onClick={onDelete}
                    >
                        <Trash className="size-4" />
                    </TooltipIconButton>
                </div>
            </div>
        </div>
    );
}
